import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Search, ShieldCheck, UserPlus, Copy, CheckCircle2, Trash2, Link2, AlertTriangle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';

const ROLES = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'check_in_staff', label: 'Check-in Staff' },
];

const ROLE_COLORS = {
  super_admin: 'bg-secondary-50 text-secondary-800 border-secondary-200',
  admin: 'bg-primary-50 text-primary-800 border-primary-200',
  moderator: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  user: 'bg-neutral-50 text-neutral-600 border-neutral-100',
};

const AdminUserRolesPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pending, setPending] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const isSuperAdmin = me?.role === 'super_admin';

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users', { params: { q: q || undefined } });
      setUsers(data.users || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []); // eslint-disable-line

  const setRole = async (userId, newRole) => {
    setPending((p) => ({ ...p, [userId]: true }));
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to change role');
    } finally {
      setPending((p) => ({ ...p, [userId]: false }));
    }
  };

  const deactivate = async (userId, email) => {
    if (userId === me?.id) return;
    if (!window.confirm(`Deactivate ${email}? They will no longer be able to sign in. This is reversible from Mongo.`)) return;
    setPending((p) => ({ ...p, [userId]: true }));
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers((us) => us.map((u) => (u.id === userId ? { ...u, is_active: false } : u)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to deactivate');
    } finally {
      setPending((p) => ({ ...p, [userId]: false }));
    }
  };

  const [copiedUserId, setCopiedUserId] = useState(null);
  const copyInviteLink = async (userId, email) => {
    setPending((p) => ({ ...p, [userId]: true }));
    try {
      const { data } = await api.post(`/admin/users/${userId}/reinvite`);
      if (data?.magic_url) {
        await navigator.clipboard.writeText(data.magic_url);
        setCopiedUserId(userId);
        setTimeout(() => setCopiedUserId(null), 1800);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to generate invite link');
    } finally {
      setPending((p) => ({ ...p, [userId]: false }));
    }
  };

  const filtered = users.filter((u) => (roleFilter === 'all' || u.role === roleFilter));
  const activeUsers = filtered.filter((u) => u.is_active !== false);
  const inactiveUsers = filtered.filter((u) => u.is_active === false);
  const [activeTab, setActiveTab] = useState('active');
  const [hardDeleteUser, setHardDeleteUser] = useState(null); // user obj or null

  const renderUserRow = (u, isInactiveTab) => (
    <div key={u.id} className="flex items-center gap-4 p-4" data-testid={`user-row-${u.id}`}>
      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${isInactiveTab ? 'from-neutral-400 to-neutral-500' : 'from-primary-600 to-primary-800'} flex items-center justify-center text-white font-semibold text-sm shadow-soft flex-shrink-0`}>
        {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-primary-900 truncate">
          {u.first_name || ''} {u.last_name || ''}
          {u.id === me?.id && <span className="ml-2 text-xs text-secondary-700">(you)</span>}
          {isInactiveTab && <Badge variant="outline" className="ml-2 text-[10px] border-accent-200 text-accent-700 bg-accent-50">Inactive</Badge>}
        </div>
        <div className="text-xs text-neutral-500 truncate">{u.email}</div>
        {u.last_login && <div className="text-[10px] text-neutral-400">Last login {new Date(u.last_login).toLocaleDateString()}</div>}
      </div>
      <Badge variant="outline" className={`${ROLE_COLORS[u.role] || ROLE_COLORS.user} capitalize`}>
        {u.role?.replace(/_/g, ' ')}
      </Badge>
      {isSuperAdmin && u.id !== me?.id && !isInactiveTab && (
        <div className="w-40">
          <Select value={u.role} onValueChange={(v) => setRole(u.id, v)} disabled={!!pending[u.id]}>
            <SelectTrigger data-testid={`user-role-select-${u.id}`}><SelectValue placeholder="Change role" /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {isSuperAdmin && u.id !== me?.id && !isInactiveTab && u.created_via === 'admin_invite' && u.is_verified !== true && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => copyInviteLink(u.id, u.email)}
          disabled={!!pending[u.id]}
          className="text-primary-700 hover:text-primary-900 hover:bg-primary-50"
          data-testid={`user-copy-invite-btn-${u.id}`}
          title="Regenerate and copy invite link"
        >
          {copiedUserId === u.id ? <CheckCircle2 className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
        </Button>
      )}
      {isSuperAdmin && u.id !== me?.id && !isInactiveTab && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => deactivate(u.id, u.email)}
          disabled={!!pending[u.id]}
          className="text-accent-700 hover:text-accent-900 hover:bg-accent-50"
          data-testid={`user-delete-btn-${u.id}`}
          title="Deactivate user"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
      {isSuperAdmin && u.id !== me?.id && isInactiveTab && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setHardDeleteUser(u)}
          className="text-accent-700 hover:text-accent-900 hover:bg-accent-50 border-accent-300"
          data-testid={`user-hard-delete-btn-${u.id}`}
          title="Delete permanently"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete permanently
        </Button>
      )}
    </div>
  );

  return (
    <div className="p-8 space-y-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Users</h1>
          <p className="text-neutral-600 text-sm">Manage roles and admin access.</p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="invite-user-btn">
              <UserPlus className="h-4 w-4 mr-1.5" /> Invite user
            </Button>
          </DialogTrigger>
          <InviteDialog onClose={() => { setInviteOpen(false); fetchUsers(); }} isSuperAdmin={isSuperAdmin} />
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            placeholder="Search email or name…" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
            className="pl-9"
            data-testid="users-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-40" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchUsers}>Search</Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="users-tabs">
          <TabsTrigger value="active" data-testid="users-tab-active">
            Active <span className="ml-1.5 text-xs text-neutral-500">({activeUsers.length})</span>
          </TabsTrigger>
          <TabsTrigger value="inactive" data-testid="users-tab-inactive">
            Inactive <span className="ml-1.5 text-xs text-neutral-500">({inactiveUsers.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          <Card className="border shadow-soft">
            <CardContent className="p-0 divide-y divide-neutral-100">
              {loading ? (
                <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
              ) : activeUsers.length === 0 ? (
                <div className="p-10 text-center text-neutral-500">No active users found.</div>
              ) : (
                activeUsers.map((u) => renderUserRow(u, false))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inactive" className="mt-4">
          <Card className="border shadow-soft">
            <CardContent className="p-0 divide-y divide-neutral-100">
              {loading ? (
                <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" /></div>
              ) : inactiveUsers.length === 0 ? (
                <div className="p-10 text-center text-neutral-500">No inactive users. Deactivated users appear here.</div>
              ) : (
                inactiveUsers.map((u) => renderUserRow(u, true))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <HardDeleteUserDialog
        user={hardDeleteUser}
        onClose={() => setHardDeleteUser(null)}
        onDeleted={() => { setHardDeleteUser(null); fetchUsers(); }}
      />

      {!isSuperAdmin && (
        <div className="flex items-start gap-2 text-sm text-neutral-500 max-w-2xl">
          <ShieldCheck className="h-4 w-4 mt-0.5 text-neutral-400" />
          <span>You can view users and invite new admins, but only a Super Admin can change existing roles.</span>
        </div>
      )}
    </div>
  );
};

const HardDeleteUserDialog = ({ user, onClose, onDeleted }) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const canConfirm = typed === 'DELETE';

  useEffect(() => {
    if (!user) { setTyped(''); setResult(null); setBusy(false); }
  }, [user]);

  const doDelete = async () => {
    if (!user || !canConfirm) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}/hard-delete`);
      setResult(data);
      setTimeout(() => onDeleted && onDeleted(), 1600);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="hard-delete-user-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-accent-700">
                <AlertTriangle className="h-5 w-5" />
                <span data-testid="hard-delete-title">Delete {user?.email} permanently?</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-1">
              <p className="text-sm text-neutral-700 leading-relaxed">
                This will remove the user, their profile, their RSVPs, their photos, and all history from the database.
                <span className="block mt-1 font-semibold text-accent-700">This cannot be undone.</span>
              </p>
              <div className="text-sm text-neutral-700">
                Type <code className="px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200 font-mono font-bold">DELETE</code> below to confirm.
              </div>
              <Input
                placeholder="Type DELETE to confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="hard-delete-confirm-input"
                autoFocus
              />
            </div>
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={onClose} disabled={busy} data-testid="hard-delete-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={doDelete}
                disabled={!canConfirm || busy}
                className="bg-accent-600 hover:bg-accent-700 text-white disabled:opacity-40"
                data-testid="hard-delete-confirm-btn"
              >
                {busy ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-4 space-y-3" data-testid="hard-delete-result">
            <div className="flex items-center gap-2 text-primary-900 font-semibold">
              <CheckCircle2 className="h-5 w-5 text-success-600" /> User deleted
            </div>
            <div className="text-xs text-neutral-600 space-y-1">
              {result.cascade && Object.entries(result.cascade).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-neutral-500">{k.replace(/_/g, ' ')}</span>
                  <span className="font-mono">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InviteDialog = ({ onClose, isSuperAdmin }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.post('/admin/users/invite', {
        email: email.trim(),
        role,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
      });
      setResult(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to invite');
    } finally { setSaving(false); }
  };

  const copyLink = async () => {
    const link = result?.magic_url || result?.magic_url_fallback;
    if (link) {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Invite user</DialogTitle></DialogHeader>
      {!result ? (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" data-testid="invite-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">First name (optional)</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Last name (optional)</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
              </SelectContent>
            </Select>
            {!isSuperAdmin && (
              <p className="text-[11px] text-neutral-500 mt-1">Only a super admin can grant super admin.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !email.trim()}
              className="bg-primary-800 hover:bg-primary-900 text-white"
              data-testid="invite-submit-btn"
            >
              {saving ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <div className="space-y-4">
          {result.existing ? (
            <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
              {result.detail}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-success-800 text-sm">
                <CheckCircle2 className="h-4 w-4" /> Invite created for <span className="font-semibold">{result.email}</span> as <span className="capitalize">{result.role?.replace(/_/g, ' ')}</span>.
              </div>
              {result.invite_sent ? (
                <div className="space-y-2">
                  <p className="text-sm text-neutral-600">A magic-link invite has been emailed. It expires in 7 days.</p>
                  <p className="text-xs text-neutral-500">You can also copy the invite link to share out-of-band:</p>
                  <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center gap-2">
                    <code className="text-xs text-primary-800 break-all flex-1" data-testid="invite-magic-url">{result.magic_url}</code>
                    <Button size="sm" variant="outline" onClick={copyLink} data-testid="invite-copy-link-btn">
                      <Copy className="h-3.5 w-3.5 mr-1" /> {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-neutral-600">Email transport isn&apos;t configured. Copy the invite link below and send it to them manually.</p>
                  <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 flex items-center gap-2">
                    <code className="text-xs text-primary-800 break-all flex-1" data-testid="invite-magic-url">{result.magic_url || result.magic_url_fallback}</code>
                    <Button size="sm" variant="outline" onClick={copyLink} data-testid="invite-copy-link-btn">
                      <Copy className="h-3.5 w-3.5 mr-1" /> {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button onClick={onClose} className="bg-primary-800 hover:bg-primary-900 text-white">Done</Button>
          </DialogFooter>
        </div>
      )}
    </DialogContent>
  );
};

export default AdminUserRolesPage;
